/**
 * Microsoft 365 tenants (Admin → Settings → Microsoft 365 Tenants).
 *
 * A tenant is one Entra ID directory plus the app registration (client id +
 * client secret) the helpdesk uses to read its shared mailboxes through
 * Microsoft Graph. Each tenant has one or more helpdesk mailboxes; every
 * mailbox feeds the SAME email-to-ticket pipeline.
 *
 * Two kinds of tenant exist:
 *  - `ENVIRONMENT`: the tenant configured by the server's environment
 *    (GRAPH_TENANT_ID / GRAPH_CLIENT_ID / secret or certificate). Created
 *    automatically at API start so existing installs keep working. Its
 *    credentials stay in the server configuration; only its display name,
 *    enabled flag and extra mailboxes can be changed here. It cannot be
 *    deleted.
 *  - `DATABASE`: added by the IT Administrator. The client secret is
 *    encrypted (AES-256-GCM) before it is stored and is never returned.
 *
 * SECURITY: no type in this file carries a client secret in a RESPONSE. The
 * secret travels only in the create/update REQUEST bodies.
 */
import { z } from 'zod';
/** Entra directory (tenant) ids and application (client) ids are GUIDs. */
export const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
export const M365_TENANT_NAME_MAX = 100;
export const M365_MAILBOX_NAME_MAX = 100;
/** Entra client secrets are ~40 chars; a generous cap still stops abuse. */
export const M365_CLIENT_SECRET_MAX = 512;
export const M365_CREDENTIAL_SOURCES = ['ENVIRONMENT', 'DATABASE'];
/**
 * Health of a tenant connection, from the last connection test or the last
 * mailbox sync (whichever is newer). Never contains Microsoft's free text.
 */
export const M365_CONNECTION_STATUSES = [
    'UNKNOWN',
    'CONNECTED',
    'AUTH_FAILED',
    'INSUFFICIENT_PERMISSIONS',
    'CONFIG_INVALID',
    'GRAPH_UNAVAILABLE',
    'SYNC_ERROR',
];
export const M365_MAILBOX_SYNC_STATUSES = ['NEVER', 'OK', 'ERROR'];
const guid = (label) => z
    .string()
    .max(100)
    .transform((v) => v.trim().toLowerCase())
    .pipe(z.string().regex(GUID_RE, `${label} must be a GUID like 00000000-0000-0000-0000-000000000000.`));
export const m365DirectoryTenantIdSchema = guid('Tenant ID');
export const m365ClientIdSchema = guid('Client ID');
const displayName = (max) => z
    .string()
    .max(max * 2)
    .transform((v) => v.trim())
    .pipe(z.string().min(1, 'Name is required.').max(max, `Use ${max} characters or fewer.`).regex(/^[^<>\u0000-\u001f]*$/, 'Name contains characters that are not allowed.'));
/** Not trimmed (a secret may legitimately start/end with any character), but blank is rejected. */
export const m365ClientSecretSchema = z
    .string()
    .min(1, 'Client secret is required.')
    .max(M365_CLIENT_SECRET_MAX, `Use ${M365_CLIENT_SECRET_MAX} characters or fewer.`)
    .refine((v) => v.trim().length > 0, 'Client secret is required.')
    .refine((v) => !/[\u0000-\u001f\u007f]/.test(v), 'Client secret contains characters that are not allowed.');
/**
 * A plain mailbox address; lower-cased. Used inside Graph URL paths and the
 * subscription resource string, so the local-part alphabet is deliberately
 * narrow (no `/ ? # % &`).
 */
export const m365MailboxAddressSchema = z
    .string()
    .max(320)
    .transform((v) => v.trim().toLowerCase())
    .pipe(z
    .string()
    .min(3, 'Mailbox address is required.')
    .max(254, 'Mailbox address is too long.')
    .regex(/^[a-z0-9._'+-]+@[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/, 'Enter a valid mailbox address, e.g. helpdesk@example.com.'));
export const adminCreateM365TenantDtoSchema = z
    .object({
    displayName: displayName(M365_TENANT_NAME_MAX),
    directoryTenantId: m365DirectoryTenantIdSchema,
    clientId: m365ClientIdSchema,
    clientSecret: m365ClientSecretSchema,
})
    .strict();
/**
 * PATCH. Omit `clientSecret` to keep the saved secret; send a value to
 * replace (rotate) it. For an ENVIRONMENT tenant only `displayName` may be
 * sent (the server rejects the rest).
 */
export const adminUpdateM365TenantDtoSchema = z
    .object({
    displayName: displayName(M365_TENANT_NAME_MAX).optional(),
    directoryTenantId: m365DirectoryTenantIdSchema.optional(),
    clientId: m365ClientIdSchema.optional(),
    clientSecret: m365ClientSecretSchema.optional(),
})
    .strict()
    .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to update.' });
export const adminCreateM365MailboxDtoSchema = z
    .object({
    address: m365MailboxAddressSchema,
    displayName: displayName(M365_MAILBOX_NAME_MAX).nullish().transform((v) => v ?? null),
})
    .strict();
export const adminUpdateM365MailboxDtoSchema = z
    .object({
    displayName: displayName(M365_MAILBOX_NAME_MAX).nullable().optional(),
    isEnabled: z.boolean().optional(),
})
    .strict()
    .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to update.' });
//# sourceMappingURL=m365-tenant-dto.js.map